/**
 * LiveStreamingControls — the broadcaster-only control column.
 *
 * Web port of the Flutter `_streamActions` widget
 * (`lib/presentation/screens/community/live_streaming_screen.dart:1464-1635`): a
 * right-aligned vertical `Column` of four circular icon buttons —
 *   - switch camera → `switchCamera()` (`:1483-1505`, `switch-camera.png`);
 *   - mute/unmute  → `muteLocalAudioStream(!_isMute)` (`:1506-1549`, `mic.png`),
 *     with a strike line over the icon when muted (`:1530-1543`);
 *   - camera off   → `muteLocalVideoStream(!_isCameraOff)` (`:1550-1593`,
 *     `video_cam.png`), with a strike line over the icon when off (`:1574-1587`);
 *   - end live     → opens a confirm sheet, then `onBackPress()` on confirm
 *     (`:1594-1630`, `hang-up.png`).
 *
 * Parity + deviation scope (`presentation.md` "Web vs Flutter UI parity scope"):
 * business logic matches Flutter (the taps map onto the Task-10 `useLiveStreaming`
 * actions `toggleMute` / `toggleCamera` / `switchCamera`, and `onEndLive`); the
 * chrome reuses the `VideoCallControls` idiom (bare `AssetIcon` buttons, the
 * rotated white strike over the mic/cam icon). The strike is Flutter's
 * `Transform.rotate(-pi/3)` 1.5px line (`:1531-1537`), rendered on the web as an
 * absolutely-positioned rotated `theme.white` line. Icons are always `theme.white`
 * (Flutter `AppColors.white` on every control, `:1500`/`:1528`/`:1572`/`:1625`)
 * over the dark video surface, in both themes.
 *
 * Ownership: this component is PRESENTATIONAL — the end-live confirm sheet is NOT
 * fired here. Flutter shows a `ConfirmSheet` before ending (`:1595-1610`); on the
 * web the page (Task 14) owns that sheet (hook/page owns navigation + sheets,
 * `presentation.md`; lessons ledger "Hooks/components never fire sheets/navigate"),
 * so `onEndLive` reports the tap and the page runs confirm-then-pop.
 *
 * Visibility: the page (Task 14) mounts this ONLY for the broadcaster and only
 * when `!clearScreen && !streamingEnded && !isOpeningChannel` (Flutter's
 * `_isBroadcaster && !_clearScreen && !_liveStreamingEnded && !_isOpeningChannel`
 * gate, `:1466-1471`), so this component neither re-derives that gate nor a role
 * gate. Switch-camera is disabled when `< 2` cameras are enumerated (the
 * video-call precedent; Flutter always shows it, the web gates on ≥2 devices).
 *
 * Theme/responsive/l10n + `qaAttr` are mandatory (`presentation.md`); the four
 * icon-only buttons carry their localized name as the button `aria-label`.
 */

import React from 'react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

export interface LiveStreamingControlsProps {
  /** `_isMute` — draws the strike over the mic icon (`:1530`). */
  isMute: boolean;
  /** `_isCameraOff` — draws the strike over the camera icon (`:1574`). */
  isCameraOff: boolean;
  /**
   * Number of available cameras, enumerated once by `useLiveStreaming` and
   * forwarded through the page. The switch-camera button is disabled when `< 2`.
   */
  cameraCount: number;
  /** Toggle mic mute (`useLiveStreaming.toggleMute`). */
  onToggleMute: () => void | Promise<void>;
  /** Toggle camera on/off (`useLiveStreaming.toggleCamera`). */
  onToggleCamera: () => void | Promise<void>;
  /** Cycle the camera device (`useLiveStreaming.switchCamera`). */
  onSwitchCamera: () => void | Promise<void>;
  /**
   * End-live tap. The confirm gate lives on the page (the page shows the
   * `ConfirmSheet` and pops on confirm, Flutter `:1595-1610`), so this button
   * reports the tap directly.
   */
  onEndLive: () => void;
}

/**
 * A single circular control button (Flutter's `Container` circle around an
 * `Image.asset`, `:1489-1503`): a `theme.button.round` disc centring its white
 * icon, with an optional rotated strike overlay for the toggled state.
 */
interface RoundControlButtonProps {
  qaId: string;
  qaValue?: string;
  ariaLabel: string;
  ariaPressed?: boolean;
  iconPath: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const RoundControlButton: React.FC<RoundControlButtonProps> = ({
  qaId,
  qaValue,
  ariaLabel,
  ariaPressed,
  iconPath,
  active = false,
  disabled = false,
  onClick,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <button
      {...qaAttr({ id: qaId, ...(qaValue !== undefined ? { value: qaValue } : {}) })}
      type="button"
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      disabled={disabled}
      onClick={onClick}
      style={{
        position: 'relative',
        width: responsive.liveStreaming.controlButtonSize,
        height: responsive.liveStreaming.controlButtonSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        padding: 0,
        backgroundColor: theme.button.round,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <AssetIcon
        path={iconPath}
        width={responsive.liveStreaming.controlIconSize}
        height={responsive.liveStreaming.controlIconSize}
        color={theme.white}
      />
      {active && (
        <div
          style={{
            position: 'absolute',
            width: responsive.liveStreaming.controlStrikeWidth,
            height: responsive.liveStreaming.controlIconSize,
            backgroundColor: theme.white,
            transform: `rotate(${ANIMATION_CONSTANTS.rotation.strikeThrough}deg)`,
            pointerEvents: 'none',
          }}
        />
      )}
    </button>
  );
};

export const LiveStreamingControls: React.FC<LiveStreamingControlsProps> = ({
  isMute,
  isCameraOff,
  cameraCount,
  onToggleMute,
  onToggleCamera,
  onSwitchCamera,
  onEndLive,
}) => {
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const copy = l10n.community.liveStreamingPage;

  // The switch-camera button is disabled when fewer than two cameras are present
  // (the video-call precedent; Flutter always shows it, the web gates on ≥2
  // enumerated devices).
  const canSwitchCamera = cameraCount >= 2;

  return (
    <div
      {...qaAttr({ id: 'live-streaming-controls' })}
      style={{
        // Right-aligned vertical column, floated off the top-right corner
        // (Flutter `Positioned(top: toolbarHeight + viewPadding.top + 12,
        // right: 8)`, `:1472-1477`). The top offset clears the top bar + the
        // device safe-area inset; the right inset matches the top-bar watcher
        // pill's right edge (`spacing.sm`) so the pill and the control buttons
        // form one right-aligned vertical line (user-review Finding 6).
        position: 'absolute',
        top: `calc(${responsive.safeArea.top} + ${responsive.topBar.height}px + ${responsive.liveStreaming.controlColumnTopGap}px)`,
        right: responsive.spacing.sm,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: responsive.liveStreaming.controlColumnGap,
        zIndex: 6,
      }}
    >
      {/* Switch camera (Flutter `:1483-1505`). */}
      <RoundControlButton
        qaId="live-streaming-switch-camera"
        qaValue={String(cameraCount)}
        ariaLabel={copy.switchCameraLabel}
        iconPath="/assets/images/switch-camera.png"
        disabled={!canSwitchCamera}
        onClick={() => {
          if (canSwitchCamera) {
            void onSwitchCamera();
          }
        }}
      />

      {/* Mute / unmute (Flutter `:1506-1549`). Strike line when muted. */}
      <RoundControlButton
        qaId="live-streaming-mute"
        qaValue={isMute ? 'muted' : 'unmuted'}
        ariaLabel={copy.muteLabel}
        ariaPressed={isMute}
        iconPath="/assets/images/mic.png"
        active={isMute}
        onClick={() => void onToggleMute()}
      />

      {/* Camera on / off (Flutter `:1550-1593`). Strike line when off. */}
      <RoundControlButton
        qaId="live-streaming-camera-off"
        qaValue={isCameraOff ? 'off' : 'on'}
        ariaLabel={copy.cameraLabel}
        ariaPressed={isCameraOff}
        iconPath="/assets/images/video_cam.png"
        active={isCameraOff}
        onClick={() => void onToggleCamera()}
      />

      {/* End live (Flutter `:1594-1630`). The page opens the confirm sheet. */}
      <RoundControlButton
        qaId="live-streaming-end"
        ariaLabel={copy.endLiveLabel}
        iconPath="/assets/images/hang-up.png"
        onClick={onEndLive}
      />
    </div>
  );
};
